import * as turf from '@turf/turf'

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_PARKING_PATH_LINE_SPACING_METERS = 21
export const DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS = 2
export const PATH_CENTER_LINE_MODE_CENTER = 'center'
export const PATH_CENTER_LINE_MODE_OFFSET = 'offset'

// ═══════════════════════════════════════════════════════════════════════════
// SMALL HELPERS
// ═══════════════════════════════════════════════════════════════════════════

// Round to 9 decimals (~0.1mm on Earth)
function roundCoord(c) {
    return Math.round(c * 1e9) / 1e9
}

// Keep bearing in 0-360 range
function normalizeBearing(bearing) {
    return ((bearing % 360) + 360) % 360
}

// Smallest angle between two bearings (0-180)
function angleDiff(a, b) {
    const d = Math.abs((((a - b) % 360) + 360) % 360)
    return d > 180 ? 360 - d : d
}

// PrimeVue SelectButton may return { value } instead of string
function unwrapSelectButton(value) {
    if (value && typeof value === 'object' && value.value !== undefined) {
        return value.value
    }
    return value
}

// Turn form input into clean options
export function normalizeCenterLineOptions(options = {}) {
    const positionRaw = unwrapSelectButton(options.position)
    const position = positionRaw === PATH_CENTER_LINE_MODE_OFFSET || positionRaw === 'offset'
        ? PATH_CENTER_LINE_MODE_OFFSET
        : PATH_CENTER_LINE_MODE_CENTER

    const offsetSide = unwrapSelectButton(options.offsetSide) === 'b' ? 'b' : 'a'

    const raw = options.offsetFromSideMeters
    const parsed = typeof raw === 'number' ? raw : Number(raw)
    const offsetFromSideMeters = Number.isFinite(parsed) && parsed >= 0
        ? parsed
        : DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS

    return { position, offsetSide, offsetFromSideMeters }
}

/**
 * Row spacing is center-to-center along the main (orange) line (= implement width on that axis).
 * End margin = half spacing so the first/last row fits at the polygon edge.
 */
function computeParkingRowLayout(usableWidth, spacingMeters) {
    if (spacingMeters <= 0) {
        return { count: 0, startOffset: 0 }
    }

    const endMargin = spacingMeters / 2
    const innerSpan = usableWidth - 2 * endMargin

    if (innerSpan < 0) {
        return { count: 0, startOffset: 0 }
    }

    const count = innerSpan < spacingMeters
        ? 1
        : 1 + Math.floor(innerSpan / spacingMeters)

    // Center row group on main line; end gaps stay equal and >= endMargin
    const startOffset = (usableWidth - (count - 1) * spacingMeters) / 2

    return { count, startOffset }
}

// ═══════════════════════════════════════════════════════════════════════════
// RECTANGLE SIZE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

// Get 4 edges of rectangle: start, end, length, bearing
function getEdges(polygon) {
    const ring = polygon.geometry.coordinates[0]
    const points = ring.slice(0, -1) // skip last point (same as first)
    
    return points.map((start, i) => {
        const end = points[(i + 1) % points.length]
        return {
            start,
            end,
            length: turf.distance(start, end, { units: 'meters' }),
            bearing: turf.bearing(turf.point(start), turf.point(end)),
        }
    })
}

// Find direction of longest edge
function getPrimaryBearing(edges) {
    const longest = edges.reduce((best, e) => e.length > best.length ? e : best)
    return normalizeBearing(longest.bearing)
}

/**
 * Get rectangle size: center, width, height, bearing
 * 
 * Width = size along main direction (bearing)
 * Height = size across (90 deg from bearing)
 */
export function getRectangleDimensions(rectangleFeature, referenceBearing = null) {
    const edges = getEdges(rectangleFeature)
    if (edges.length < 4) return null

    const center = turf.centroid(rectangleFeature)
    const bearing = referenceBearing ?? getPrimaryBearing(edges)

    // Split edges: parallel to bearing vs perpendicular
    const parallelEdges = []
    const perpEdges = []
    
    for (const edge of edges) {
        const diff = angleDiff(edge.bearing, bearing)
        if (diff < 45 || diff > 135) {
            parallelEdges.push(edge)
        } else {
            perpEdges.push(edge)
        }
    }

    // Width = average of parallel edges
    // Height = average of perpendicular edges
    const width = parallelEdges.length > 0
        ? parallelEdges.reduce((sum, e) => sum + e.length, 0) / parallelEdges.length
        : edges[0].length
    
    const height = perpEdges.length > 0
        ? perpEdges.reduce((sum, e) => sum + e.length, 0) / perpEdges.length
        : edges[1]?.length ?? width

    return {
        center: center.geometry.coordinates,
        width,
        height,
        bearing,
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// PARKING PATH LINES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Make parking lines inside rectangle.
 * 
 * Simple logic:
 * 1. Get rectangle size (width, height, bearing)
 * 2. Center line = line along bearing, at center or offset from side
 * 3. Cross lines = lines across center line, evenly spaced
 * 
 * @param {Feature<Polygon>} rectangleFeature - The rectangle
 * @param {number} spacingMeters - Space between cross lines
 * @param {Object} centerLineOptions - Where to put center line
 * @returns {Feature<MultiLineString>} - All parking lines
 */
export function createParkingPathInRectangle(
    rectangleFeature,
    spacingMeters = DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
    centerLineOptions = {},
) {
    const dims = getRectangleDimensions(rectangleFeature)
    if (!dims) {
        throw new Error('Cannot get rectangle size')
    }

    const { center, width, height, bearing } = dims
    const { position, offsetSide, offsetFromSideMeters } = normalizeCenterLineOptions(centerLineOptions)

    // Main direction = bearing, cross direction = 90 deg from bearing
    const crossBearing = normalizeBearing(bearing + 90)

    // Lines use full rectangle width/height (edge to edge)
    const usableWidth = width
    if (usableWidth <= 0) {
        throw new Error('Rectangle too small (width)')
    }

    const usableHeight = height
    if (usableHeight <= 0) {
        throw new Error('Rectangle too small (height)')
    }

    // Where center line sits and which way cross lines go
    let centerLineOffset = 0
    let crossDirection = crossBearing
    let crossLength = usableHeight / 2

    if (position === PATH_CENTER_LINE_MODE_OFFSET) {
        // Center line near one side, cross lines go to other side
        const halfHeight = height / 2
        const maxOffset = height

        if (offsetFromSideMeters > maxOffset) {
            throw new Error('Distance from side leaves no room for parking lines')
        }

        if (offsetSide === 'a') {
            // Near Side A, lines go toward Side B (negative offset)
            centerLineOffset = -halfHeight + offsetFromSideMeters
            crossDirection = crossBearing
            crossLength = height - offsetFromSideMeters
        } else {
            // Near Side B, lines go toward Side A (positive offset)
            centerLineOffset = halfHeight - offsetFromSideMeters
            crossDirection = normalizeBearing(crossBearing + 180)
            crossLength = height - offsetFromSideMeters
        }
    }

    if (crossLength <= 0) {
        throw new Error('No room for cross lines')
    }

    const lines = []
    const centerPoint = turf.point(center)

    // Move center line if offset
    const centerLineStart = centerLineOffset !== 0
        ? turf.destination(centerPoint, Math.abs(centerLineOffset) / 1000, 
            centerLineOffset > 0 ? crossBearing : normalizeBearing(crossBearing + 180), 
            { units: 'kilometers' })
        : centerPoint

    // Build center line from one end to other
    const halfUsableWidth = usableWidth / 2
    const lineStart = turf.destination(centerLineStart, halfUsableWidth / 1000, 
        normalizeBearing(bearing + 180), { units: 'kilometers' })
    const lineEnd = turf.destination(centerLineStart, halfUsableWidth / 1000, 
        bearing, { units: 'kilometers' })

    const centerLineCoords = [
        [roundCoord(lineStart.geometry.coordinates[0]), roundCoord(lineStart.geometry.coordinates[1])],
        [roundCoord(lineEnd.geometry.coordinates[0]), roundCoord(lineEnd.geometry.coordinates[1])],
    ]
    lines.push(centerLineCoords)

    // Cross lines along main line, centered on orange (equal cap gaps when extra space)
    /**
     * Example: usable 131 m, spacing 20 m
     *   count = 6, startOffset = (131 - 5*20) / 2 = 15.5 m
     *   blue lines at 15.5, 35.5, …, 115.5 m (15.5 m margin each cap)
     */
    const { count, startOffset } = computeParkingRowLayout(usableWidth, spacingMeters)
    if (count > 0) {
        const centerLineString = turf.lineString(centerLineCoords)

        for (let i = 0; i < count; i++) {
            const distance = startOffset + i * spacingMeters
            const station = turf.along(centerLineString, distance / 1000, { units: 'kilometers' })

            if (position === PATH_CENTER_LINE_MODE_OFFSET) {
                // One cross line in one direction
                const crossEnd = turf.destination(station, crossLength / 1000, crossDirection, { units: 'kilometers' })
                lines.push([
                    [roundCoord(station.geometry.coordinates[0]), roundCoord(station.geometry.coordinates[1])],
                    [roundCoord(crossEnd.geometry.coordinates[0]), roundCoord(crossEnd.geometry.coordinates[1])],
                ])
            } else {
                // Cross line in both directions (centered)
                const crossEndA = turf.destination(station, crossLength / 1000, crossBearing, { units: 'kilometers' })
                const crossEndB = turf.destination(station, crossLength / 1000, 
                    normalizeBearing(crossBearing + 180), { units: 'kilometers' })
                lines.push([
                    [roundCoord(crossEndB.geometry.coordinates[0]), roundCoord(crossEndB.geometry.coordinates[1])],
                    [roundCoord(crossEndA.geometry.coordinates[0]), roundCoord(crossEndA.geometry.coordinates[1])],
                ])
            }
        }
    }

    return turf.multiLineString(lines)
}
